import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import type { Editor } from '@tiptap/react';
import { apiGet } from '../../lib/api';
import { getInitials } from '../../lib/people';
import { createFlagType, flagTypeById, shortcutTypes } from '../../lib/flags';
import { useFlagStore } from '../../contexts/FlagStateContext';
import type { DocRef } from '../../contexts/ExcerptSyncContext';
import { findFlag, textblockAt, type FlagEntry, type FlagExtensionOptions } from '../editor/FlagExtension';
import { FlagMenu } from './FlagMenu';
import { FlagPopover } from './FlagPopover';
import { FlagTypeForm } from './FlagTypeForm';

/**
 * Connects one editor to flags (#316). The extension's options must exist
 * before `useEditor` runs and stay stable for the editor's life, so they call
 * through refs; `FlagEditorLayer` fills those refs and owns the menu and the
 * popover.
 */

type MenuRequest = Parameters<FlagExtensionOptions['onOpenMenu']>[0] & { changeFlagId?: string };
type FlagRequest = Parameters<FlagExtensionOptions['onOpenFlag']>[0];

export interface FlagBridge {
  options: Partial<FlagExtensionOptions>;
  handlers: MutableRefObject<{ openMenu: (a: MenuRequest) => void; openFlag: (a: FlagRequest) => void }>;
  people: MutableRefObject<Map<string, string>>;
}

export function useFlagBridge(host: DocRef | undefined): FlagBridge {
  const store = useFlagStore();
  const handlers = useRef({ openMenu: (_a: MenuRequest) => {}, openFlag: (_a: FlagRequest) => {} });
  const people = useRef(new Map<string, string>());
  const enabled = !!host;
  const options = useMemo<Partial<FlagExtensionOptions>>(() => (enabled ? {
    enabled: true,
    getState: (id) => store.get(id),
    getType: (id) => flagTypeById(store.types(), id),
    getShortcutTypes: () => shortcutTypes(store.types()),
    personInitials: (id) => { const n = people.current.get(id); return n ? getInitials(n) : '?'; },
    personName: (id) => people.current.get(id) ?? '',
    onOpenMenu: (a) => handlers.current.openMenu(a),
    onOpenFlag: (a) => handlers.current.openFlag(a),
  } : { enabled: false }), [enabled, store]);
  return { options, handlers, people };
}

/** Opens the menu for the line at the cursor; used by the toolbar's Flag button. */
export function openMenuAtCursor(editor: Editor, bridge: FlagBridge, anchor: { left: number; bottom: number }): void {
  const block = textblockAt(editor.state, editor.state.selection.head);
  bridge.handlers.current.openMenu({ pos: block?.pos ?? 0, x: anchor.left, y: anchor.bottom + 4, mode: 'kinds' });
}

function goTo(path: string) {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function FlagEditorLayer({ editor, host, bridge, onAddToList }: {
  editor: Editor; host: DocRef; bridge: FlagBridge;
  /** Named lists (#337): file the menu's line into a list, from the right-click menu. */
  onAddToList?: (a: { positions: number[]; x: number; y: number }) => void;
}) {
  const store = useFlagStore();
  const [menu, setMenu] = useState<MenuRequest | null>(null);
  const [pop, setPop] = useState<FlagRequest | null>(null);

  useEffect(() => store.registerSource(host), [store, host.type, host.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redraw the margin whenever flag state or types change.
  useEffect(() => store.subscribe(() => { if (!editor.isDestroyed) editor.commands.refreshFlagDecorations(); }), [store, editor]);

  useEffect(() => {
    let live = true;
    apiGet<Array<{ id: string; name: string }>>('/people').then((list) => {
      if (!live) return;
      bridge.people.current = new Map(list.map((p) => [p.id, p.name]));
      if (!editor.isDestroyed) editor.commands.refreshFlagDecorations();
    }).catch(() => {});
    return () => { live = false; };
  }, [bridge, editor]);

  useEffect(() => {
    bridge.handlers.current = {
      openMenu: (a) => { setPop(null); setMenu(a); },
      openFlag: (a) => { setMenu(null); setPop(a); },
    };
  }, [bridge]);

  // Find and modules link here with ?flag=<id>: open that flag once.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('flag');
    if (!id) return;
    const t = setTimeout(() => { if (!editor.isDestroyed) editor.commands.revealFlag(id, true); }, 0);
    return () => clearTimeout(t);
  }, [editor]);

  const menuBlock = menu ? (menu.changeFlagId ? findFlag(editor.state.doc, menu.changeFlagId) : textblockAt(editor.state, menu.pos)) : null;
  const menuFlags: FlagEntry[] = (menuBlock?.node.attrs.flags as FlagEntry[] | undefined) ?? [];
  const popAt = pop ? findFlag(editor.state.doc, pop.flagId) : null;
  const popFlag = popAt ? (popAt.node.attrs.flags as FlagEntry[]).find((f) => f.id === pop!.flagId) : undefined;

  return (
    <>
      {menu && (
        <FlagMenu
          x={menu.x}
          y={menu.y}
          types={store.types()}
          initialMode={menu.mode}
          title={menu.changeFlagId ? 'Change type' : undefined}
          active={{
            types: new Set(menu.changeFlagId ? [] : menuFlags.map((f) => f.type)),
            persons: new Set(menuFlags.filter((f) => f.personId).map((f) => f.personId!)),
          }}
          onPick={(type, personId) => {
            if (menu.changeFlagId) editor.chain().focus().setFlagType(menu.changeFlagId, type, personId).run();
            else editor.chain().focus().toggleFlag(type, personId, menu.pos).run();
            setMenu(null);
          }}
          onClear={!menu.changeFlagId && menuFlags.length ? () => { editor.chain().focus().clearFlags(menu.pos).run(); setMenu(null); } : undefined}
          onClose={() => setMenu(null)}
          onAddToList={onAddToList && !menu.changeFlagId ? () => { setMenu(null); onAddToList({ positions: [menu.pos + 1], x: menu.x, y: menu.y }); } : undefined}
          renderNewType={menu.changeFlagId ? undefined : (done) => (
            <FlagTypeForm onCancel={done} onSubmit={async (input) => {
              const created = await createFlagType(input);
              await store.loadTypes();
              editor.chain().focus().toggleFlag(created.id, undefined, menu.pos).run();
              setMenu(null);
            }} />
          )}
        />
      )}
      {pop && popAt && popFlag && (
        <FlagPopover
          key={pop.flagId}
          flagId={pop.flagId}
          type={popFlag.type}
          rect={pop.rect}
          where="document"
          text={popAt.node.textContent}
          personName={popFlag.personId ? bridge.people.current.get(popFlag.personId) ?? null : null}
          onChangeType={(rect) => { setPop(null); setMenu({ pos: popAt.pos, x: rect.left, y: rect.bottom + 4, mode: 'kinds', changeFlagId: pop.flagId }); }}
          onRemove={() => { editor.chain().focus().removeFlag(pop.flagId).run(); setPop(null); }}
          onOpenTask={(taskId) => goTo(`/tasks/${encodeURIComponent(taskId)}`)}
          onClose={() => setPop(null)}
        />
      )}
    </>
  );
}
